/* ---------------------------------------------------------------------------
   When a bridge is due to be rated again. LIFECYCLE.md `LC-24`.

   EVERY ACCEPTED RATING CARRIES A DUE DATE: its acceptance plus the
   department's interval, the shorter one where the bridge is posted or
   restricted (TenantProfile.rerating). And a rating is due early — flagged —
   when what it rested on has moved:
     · an accepted principal, baseline, special or post-event inspection since
       has lowered a primary element's condition, against the accepted
       inspection before the rating;
     · a Critical defect has been logged since on a member a current rating rates;
     · the model of record is no longer the model a current rating was made on.
   A newer accepted rating clears the first two, since they are measured from
   the newest acceptance, and replaces the rating the third names.

   CONDITION RUNS 0 GOOD TO THE TOP OF THE SCALE WORST, the convention of
   every inspection form this register holds (DoR's 0–4, src/data/ratingGuide.ts).
   A higher number on a primary element is a lowered condition.

   DERIVED, NOT STORED: the flag is worked out from the ratings, the inspections
   and the defects each time it is drawn, so there is nothing to fall out of
   step with them.
   --------------------------------------------------------------------------- */

import type { Bridge, Inspection, RegisterDefect } from './types.ts';
import { BRIDGE_ELEMENTS } from './elements.ts';
import { acceptedOn, currentRatings, type RatingLike } from './posting.ts';
import { isOpenDefect, onRatedMember, placeOfDefect } from '../rating/damage.ts';
import { onSupersededModel } from './phase.ts';

/** How often a department re-rates, in years. Provisional in every profile. */
export interface RerateInterval {
  years: number;
  /** For a bridge that is posted or restricted. */
  restrictedYears: number;
}

export type RerateTriggerKind = 'inspection' | 'defect' | 'model' | 'interval';

export interface RerateTrigger {
  kind: RerateTriggerKind;
  /** A chip's worth: "Deck 1 → 3", "Critical defect dfk0010". */
  label: string;
  /** When it happened, where it has a date. */
  at: string | null;
}

export interface RerateStatus {
  /** The current ratings it was worked out over. Empty: never rated, nothing is due. */
  rated: number;
  /** The earliest due date across the current ratings, YYYY-MM-DD. */
  dueOn: string | null;
  triggers: RerateTrigger[];
  due: boolean;
}

/** The rating fields this reads, over those `posting.ts` does. */
export interface RerateRating extends RatingLike {
  family: string;
  modelId?: string | null;
  modelRevision?: string | null;
}

/** The primary elements: what carries the load, superstructure and substructure. */
const PRIMARY: ReadonlySet<string> = new Set(
  BRIDGE_ELEMENTS.filter((e) => e.group === 'Superstructure' || e.group === 'Substructure').map((e) => e.id),
);

const LABEL = new Map(BRIDGE_ELEMENTS.map((e) => [e.id, e.label]));

const DETAILED: ReadonlySet<string> = new Set(['principal', 'baseline', 'special', 'post-event']);

function addYears(iso: string, years: number): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  d.setUTCFullYear(d.getUTCFullYear() + years);
  return d.toISOString().slice(0, 10);
}

/** The primary elements this inspection rates worse than the one before it. */
function lowered(before: Inspection, after: Inspection): RerateTrigger[] {
  const was = new Map<string, number>();
  for (const e of before.elements ?? []) if (typeof e.rating === 'number') was.set(e.element, e.rating);
  const out: RerateTrigger[] = [];
  for (const e of after.elements ?? []) {
    if (!PRIMARY.has(e.element) || typeof e.rating !== 'number') continue;
    const prev = was.get(e.element);
    if (prev !== undefined && e.rating > prev) {
      out.push({ kind: 'inspection', label: `${LABEL.get(e.element) ?? e.element} ${prev} → ${e.rating}`, at: after.date });
    }
  }
  return out;
}

/** Whether a bridge is due to be rated again, and why. */
export function reratingOf(
  bridge: Pick<Bridge, 'id' | 'inspections' | 'loadRestriction' | 'restricted' | 'modelOfRecord'>,
  ratings: readonly RerateRating[],
  defects: readonly RegisterDefect[],
  interval: RerateInterval,
  today: string,
): RerateStatus {
  const current = currentRatings(ratings, bridge.id);
  if (current.length === 0) return { rated: 0, dueOn: null, triggers: [], due: false };

  const posted = bridge.restricted === true || bridge.loadRestriction != null;
  const dueOn = current
    .map((r) => addYears(acceptedOn(r), posted || r.postingRequired ? interval.restrictedYears : interval.years))
    .sort()[0]!;
  const since = current.map(acceptedOn).sort().reverse()[0]!.slice(0, 10);
  const triggers: RerateTrigger[] = [];

  /* An accepted principal or special inspection after the newest acceptance,
     against the accepted inspection on or before it. */
  const accepted = bridge.inspections
    .filter((i) => i.state === 'accepted' && DETAILED.has(i.type))
    .sort((a, b) => a.date.localeCompare(b.date));
  const baseline = accepted.filter((i) => i.date <= since).pop() ?? null;
  if (baseline !== null) {
    for (const later of accepted.filter((i) => i.date > since)) triggers.push(...lowered(baseline, later));
  }

  /* A Critical defect logged since, on a member one of the current ratings rates. */
  for (const d of defects) {
    if (d.bridgeId !== bridge.id || d.severity !== 'Critical' || !isOpenDefect(d)) continue;
    if (d.loggedAt.slice(0, 10) <= since) continue;
    const place = placeOfDefect(d, null);
    if (current.some((r) => onRatedMember(place, r.family, r.spanIndex, r.position))) {
      triggers.push({ kind: 'defect', label: `Critical issue ${d.id}`, at: d.loggedAt.slice(0, 10) });
    }
  }

  if (current.some((r) => onSupersededModel(r.modelId, bridge.modelOfRecord, r.modelRevision))) {
    triggers.push({ kind: 'model', label: 'Model of record changed', at: bridge.modelOfRecord?.setAt?.slice(0, 10) ?? null });
  }

  if (today >= dueOn) triggers.push({ kind: 'interval', label: `Interval passed ${dueOn}`, at: dueOn });

  return { rated: current.length, dueOn, triggers, due: triggers.length > 0 };
}
